import React, { useMemo, useState } from 'react';
import { BarList, Btn, DataTable, KPI, KPIRow, panelPost } from '../../kit';
import { AdminLink } from '../../adminNav';
import { Labeled, Modal, inputCls } from '../emailKit';
import { EXCLUDED_LABEL } from '../campaigns/shared';
import { LifecyclePill } from '../../tags/shared';
import { SECTION, Trigger, inBody, num } from './shared';
import { contentText } from './Switches';

/**
 * A trigger's dry run (email/memac_dry_run — reads only; at most a run row
 * of kind dry_run is written) and the manual send that follows it.
 *
 * "Send to these N now" reuses the Campaigns sender on the server
 * (email/memac_send → target_send): the count typed here travels as
 * expected_count, and the server re-evaluates and refuses when it no longer
 * matches — so the people sent to are the people just shown, or nobody.
 */

export type DryRun = {
  detected: number; eligible: number;
  /** How many one Send mails (the Campaigns cap, 2,000); the rest go with a second Send. */
  batch: number;
  excluded: Record<string, number>; capped: Record<string, number>;
  people: Record<string, any>[];
  notes: string[];
  at: number;
};

export const normDryRun = (d: any): DryRun => {
  const counts = (o: any): Record<string, number> => {
    const out: Record<string, number> = {};
    if (o && typeof o === 'object' && !Array.isArray(o)) {
      Object.entries(o).forEach(([k, v]) => { const n = Number(v); if (Number.isFinite(n) && n > 0) out[k] = n; });
    }
    return out;
  };
  const eligible = Number(d?.eligible ?? 0) || 0;
  const batch = Number(d?.batch_size);
  return {
    detected: Number(d?.detected ?? 0) || 0,
    eligible,
    batch: Number.isFinite(batch) && d?.batch_size !== null && d?.batch_size !== undefined ? batch : eligible,
    notes: Array.isArray(d?.notes) ? d.notes.map(String) : [],
    excluded: counts(d?.excluded),
    capped: counts(d?.capped),
    people: Array.isArray(d?.people) ? d.people : [],
    at: Date.now(),
  };
};

const REASON_LABEL: Record<string, string> = {
  ...EXCLUDED_LABEL,
  not_in_audience: 'Outside the audience',
  not_found: 'No such account',
  unsubscribed: 'Unsubscribed (this category)',
  sendgrid_unsubscribed: 'Unsubscribed in SendGrid',
  sg_unsubscribed: 'Unsubscribed in SendGrid',
  stop_signal: 'Stop condition met',
  stopped: 'Stop condition met',
  audience: 'Outside the audience',
  per_30d: 'Trigger cap (30 days)',
  max_per_30d: 'Trigger cap (30 days)',
  lifetime: 'Lifetime cap',
  max_lifetime: 'Lifetime cap',
  cooldown: 'Cooldown',
  occurrence: 'Already sent for this occurrence',
  per_occurrence: 'Already sent for this occurrence',
  status_cap: 'Status cap (non-service, 30 days)',
  max_per_run: 'Over this run’s limit',
  suspended: 'Suspended account',
};
export const reasonLabel = (k: string) => REASON_LABEL[k] || k.replace(/_/g, ' ');

const bars = (o: Record<string, number>) =>
  Object.entries(o).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ label: reasonLabel(k), value: v }));

/** The values the mail would be personalised with, in one cell. */
const valuesText = (v: any): string => {
  if (!v || typeof v !== 'object') return '';
  return Object.entries(v).filter(([, x]) => x !== null && x !== undefined && x !== '')
    .map(([k, x]) => `${k}: ${typeof x === 'object' ? JSON.stringify(x) : String(x)}`).join(' · ');
};

export const DryRunView: React.FC<{ r: DryRun }> = ({ r }) => {
  const nExcluded = Object.values(r.excluded).reduce((a, b) => a + b, 0);
  const nCapped = Object.values(r.capped).reduce((a, b) => a + b, 0);
  const rows = useMemo(() => r.people.map(p => ({
    email: p.email, name: p.name, status: p.status ?? p.lifecycle, cohort: p.cohort,
    result: p.reason ? reasonLabel(String(p.reason)) : 'would get it',
    values: valuesText(p.values), occurrence: p.occurrence, _user: p.user_id, _ok: !p.reason,
  })), [r.people]);
  return (
    <div className="flex flex-col gap-3">
      <KPIRow>
        <KPI label="Detected" value={num(r.detected)} hint="the signal fires for them" />
        <KPI label="Would get it" value={num(r.eligible)} hint="after every exclusion and cap" />
        <KPI label="Left out" value={num(nExcluded)} hint="audience, unsubscribes, bounces…" />
        <KPI label="Capped" value={num(nCapped)} hint="already had enough mail" />
      </KPIRow>
      {(nExcluded > 0 || nCapped > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          {nExcluded > 0 && <div><div className="text-xs text-grayscale-1100 mb-1">Left out, by reason</div><BarList items={bars(r.excluded)} /></div>}
          {nCapped > 0 && <div><div className="text-xs text-grayscale-1100 mb-1">Capped, by cap</div><BarList items={bars(r.capped)} /></div>}
        </div>
      )}
      {r.notes.length > 0 && (
        <ul className="text-xs text-amber-800 list-disc pl-5">{r.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
      )}
      <div className="text-xs text-grayscale-1100">
        {rows.length ? `A sample of ${num(rows.length)} (the server lists at most 50), with why each one would or would not get it.`
          : 'Nobody to show.'}
      </div>
      {rows.length > 0 && (
        <DataTable rows={rows} columns={['email', 'name', 'status', 'cohort', 'result', 'values']}
          optionalColumns={['occurrence']}
          csvName="memac_dry_run" maxHeight="22rem"
          render={{
            status: row => <LifecyclePill value={row.status} />,
            result: row => <span className={row._ok ? 'text-green-700 font-medium' : 'text-grayscale-1100'}>{row.result}</span>,
            email: row => (row.email ? (
              <AdminLink to={{ group: 'email', tab: 'people', email: String(row.email) }}
                title="Email Center → People (timeline)">{row.email}</AdminLink>
            ) : '—'),
          }} />
      )}
    </div>
  );
};

/**
 * "Send to these N now…" — a typed-count confirm, then email/memac_send.
 * Works off the last dry run's eligible count; a stale count is refused by
 * the server and the operator runs the dry run again.
 */
export const SendNow: React.FC<{
  t: Trigger; dry: DryRun | null; disabledReason?: string; onSent: (msg: React.ReactNode, ok: boolean) => void;
}> = ({ t, dry, disabledReason, onSent }) => {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // The server compares expected_count with what one Send mails (at most
  // the Campaigns cap), not with everyone eligible.
  const n = dry?.batch ?? 0;
  const rest = dry ? Math.max(0, dry.eligible - dry.batch) : 0;
  const typedOk = typed.replace(/[,\s]/g, '') === String(n);
  const why = disabledReason || (!dry ? 'Run a dry run first — the send goes to exactly the people it shows'
    : n === 0 ? 'Nobody would get it right now' : '');
  const ageMin = dry ? Math.floor((Date.now() - dry.at) / 60000) : 0;

  const send = async () => {
    if (!typedOk || busy) return;
    setBusy(true); setErr('');
    const { data, error } = await panelPost(SECTION, 'memac_send', { trigger_id: t.id, confirm: true, expected_count: n });
    setBusy(false);
    if (error) { setErr(error); return; }
    setOpen(false); setTyped('');
    const sendId = String(data?.send_id || data?.result?.send_id || '');
    const queued = data?.queued ?? data?.result?.queued ?? data?.count ?? n;
    const left = Number(data?.remaining ?? 0);
    onSent(<>
      Started: {num(queued)} e-mail{Number(queued) === 1 ? '' : 's'} queued for {t.id}.
      {left > 0 && ` ${num(left)} more are eligible — run the dry run again and Send to mail them.`}
      {sendId && <> It is a campaign now — <AdminLink to={{ group: 'email', tab: 'campaigns', campaign: sendId }}>
        open it in Campaigns</AdminLink> for its funnel.</>}
    </>, true);
  };

  return (
    <>
      <Btn kind="danger" onClick={() => { setTyped(''); setErr(''); setOpen(true); }} disabled={!!why}
        title={why || `E-mail the ${num(n)} people the dry run lists as getting it`}>
        {`Send to these ${dry ? num(n) : 'N'} now…`}
      </Btn>
      {open && inBody(
        <Modal title={`Send ${t.id} to ${num(n)} ${n === 1 ? 'person' : 'people'} now?`}
          onClose={() => setOpen(false)}
          footer={<>
            <Btn onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn kind="danger" onClick={send} disabled={!typedOk || busy}>
              {busy ? 'Starting…' : `Send ${num(n)} e-mail${n === 1 ? '' : 's'}`}
            </Btn>
          </>}>
          <div className="text-xs font-semibold uppercase tracking-wide text-grayscale-1100">What will happen</div>
          <ul className="list-disc pl-5 flex flex-col gap-1">
            <li><b>{num(n)}</b> {n === 1 ? 'person gets' : 'people get'} a real e-mail now: {contentText(t)}. It cannot be recalled.</li>
            {rest > 0 && <li>{num(dry!.eligible)} are eligible; one Send mails {num(n)}. The other {num(rest)} need a second Send.</li>}
            <li>It goes through the Campaigns sender (batches, the From address and tracking of a campaign) and is
              recorded against {t.id}, so its caps count it from now on.</li>
            <li>The people are the ones the dry run {ageMin < 1 ? 'just' : `${ageMin} min ago`} said would get it. If that has
              changed, the server refuses and you run the dry run again.</li>
            <li>This does not switch the trigger on; nothing else is sent afterwards.</li>
          </ul>
          <Labeled label={`Type ${n} to confirm`}>
            <input autoFocus value={typed} onChange={e => setTyped(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && typedOk) void send(); }}
              className={`${inputCls} !w-40`} inputMode="numeric" />
          </Labeled>
          {err && <div className="text-xs text-red-700">{err}</div>}
        </Modal>
      )}
    </>
  );
};
